import { z } from 'zod'

export const STRATALYST_ORG_FORMS_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const stratalystOrgFormsReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
}).passthrough()

export const stratalystOrgFormsReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.array(z.string()),
  projection: z.literal('stratalyst-consulting-forms-admin'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const stratalystOrgFormsReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.boolean(),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const stratalystOrgFormScopeSchema = z.enum(['library', 'one_off'])

export const stratalystOrgFormSummarySchema = z.object({
  id: z.string().min(1),
  ownerOrgId: z.string().min(1),
  formKey: z.string(),
  displayName: z.string(),
  formScope: stratalystOrgFormScopeSchema,
  oneOffCompanyId: z.string().nullable(),
  analysisEnabled: z.boolean(),
  isActive: z.boolean(),
  updatedAt: z.string(),
}).passthrough()

const stratalystFieldTypeSchema = z.enum([
  'text',
  'textarea',
  'number',
  'select',
  'checkbox',
  'rating',
  'date',
  'multiselect',
])

export const stratalystOrgFormFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: stratalystFieldTypeSchema,
  required: z.boolean().optional(),
  placeholder: z.string().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() }).passthrough()).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
}).passthrough()

export const stratalystOrgFormRevisionSummarySchema = z.object({
  id: z.string().min(1),
  formRegistryId: z.string().min(1),
  version: z.number(),
  fields: z.array(stratalystOrgFormFieldSchema),
  status: z.enum(['draft', 'active', 'archived']),
  createdByUserId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const stratalystOrgFormsListResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_ORG_FORMS_READ_CONTRACT_VERSION),
  forms: z.array(stratalystOrgFormSummarySchema),
  readEvidence: stratalystOrgFormsReadEvidenceSchema,
  sideEffects: stratalystOrgFormsReadSideEffectsSchema,
}).passthrough()

export const stratalystOrgFormDetailResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_ORG_FORMS_READ_CONTRACT_VERSION),
  form: stratalystOrgFormSummarySchema,
  readEvidence: stratalystOrgFormsReadEvidenceSchema,
  sideEffects: stratalystOrgFormsReadSideEffectsSchema,
}).passthrough()

export const stratalystOrgFormRevisionsResponseSchema = z.object({
  contract_version: z.literal(STRATALYST_ORG_FORMS_READ_CONTRACT_VERSION),
  revisions: z.array(stratalystOrgFormRevisionSummarySchema),
  readEvidence: stratalystOrgFormsReadEvidenceSchema,
  sideEffects: stratalystOrgFormsReadSideEffectsSchema,
}).passthrough()

export type StratalystOrgFormsReadQuery = z.infer<typeof stratalystOrgFormsReadQuerySchema>
export type StratalystOrgFormsReadEvidence = z.infer<typeof stratalystOrgFormsReadEvidenceSchema>
export type StratalystOrgFormsReadSideEffects = z.infer<typeof stratalystOrgFormsReadSideEffectsSchema>
export type StratalystOrgFormScope = z.infer<typeof stratalystOrgFormScopeSchema>
export type StratalystOrgFormSummary = z.infer<typeof stratalystOrgFormSummarySchema>
export type StratalystOrgFormField = z.infer<typeof stratalystOrgFormFieldSchema>
export type StratalystOrgFormRevisionSummary = z.infer<typeof stratalystOrgFormRevisionSummarySchema>
export type StratalystOrgFormsListResponse = z.infer<typeof stratalystOrgFormsListResponseSchema>
export type StratalystOrgFormDetailResponse = z.infer<typeof stratalystOrgFormDetailResponseSchema>
export type StratalystOrgFormRevisionsResponse = z.infer<typeof stratalystOrgFormRevisionsResponseSchema>
